import { toast } from 'react-toastify';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getTokensFromLocalStorage } from '../../Functions/LocalStorageUtils';
import { ConfigurationI } from '../types/configurations';

export const configurationsApi = createApi({
    reducerPath: 'configurationsQuery',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_URL,
        credentials: 'include',
        prepareHeaders: (headers) => {
            const tokens = getTokensFromLocalStorage();

            if (tokens?.access_token) {
                headers.set('Authorization', `Bearer ${tokens.access_token}`);
            }

            return headers;
        }
    }),
    tagTypes: ['configurations'],
    endpoints: (builder) => ({
        getAllConfigurations: builder.query({
            query: () => ({
                url: '/v1/floorplan/configuration/get-all',
                method: 'GET'
            }),
            providesTags: ['configurations']
        }),
        saveConfiguration: builder.mutation({
            query: ({name, tech, configuration}: {name: string, tech: string, configuration: ConfigurationI}) => ({
                url: '/v1/floorplan/configuration/save',
                method: 'POST',
                body: {
                    name: name,
                    tech: tech,
                    data: configuration
                }
            }),
            async onQueryStarted({}, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(configurationsApi.util.invalidateTags(['configurations']));
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to save configuration!');
                }
            }
        }),
        deleteConfiguration: builder.mutation({
            query: (id: number) => ({
                url: '/v1/floorplan/configuration/delete',
                method: 'DELETE',
                body: { id }
            }),
            async onQueryStarted(_id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(configurationsApi.util.invalidateTags(['configurations']));
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to delete configuration!');
                }
            }
        }),
        overwriteConfiguration: builder.mutation({
            query: ({id, configuration}: {id: number, configuration: ConfigurationI}) => ({
                url: '/v1/floorplan/configuration/overwrite',
                method: 'POST',
                body: {
                    id: id,
                    data: configuration
                }
            }),
            async onQueryStarted({}, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(configurationsApi.util.invalidateTags(['configurations']));
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to overwrite configuration!');
                }
            }
        }),
        editConfigurationTitle: builder.mutation({
            query: ({ id, name }: { id: number; name: string }) => ({
                url: '/v1/floorplan/configuration/edit-title',
                method: 'POST',
                body: {
                    id,
                    name
                }
            }),
            async onQueryStarted({}, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(configurationsApi.util.invalidateTags(['configurations']));
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to rename configuration!');
                }
            }
        }),
        startIconsConfiguration: builder.mutation<{ success: boolean; url: string }, { type: string; color: string }>({
            query: ({ type, color }) => ({
                url: '/v1/floorplan/configuration/icons/start',
                method: 'POST',
                body: { type, color }
            }),
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    if (data.success) {
                        toast.success('Icons configuration started successfully!');
                        setTimeout(() => {
                            window.open(data.url, '_blank');
                        }, 500);
                    } else {
                        toast.error('Failed to start icons configuration!');
                    }
                   
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to start icons configuration!');
                }
            }
        }),
        checkIconsConfiguration: builder.mutation<{ success: boolean; image: string }, void>({
            query: () => ({
                url: '/v1/floorplan/configuration/icons/get',
                method: 'GET'
            }),
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    if(data.success){
                        toast.success('Icons configuration completed successfully!');
                    }
                } catch(error){
                    console.error(error);
                    console.log(error);
                }
            }
        })
    })
});

export const {
    useGetAllConfigurationsQuery,
    useSaveConfigurationMutation,
    useDeleteConfigurationMutation,
    useOverwriteConfigurationMutation,
    useEditConfigurationTitleMutation,
    useStartIconsConfigurationMutation,
    useCheckIconsConfigurationMutation
} = configurationsApi;